import React from "react";

/* PACKAGES */
import moment from "moment";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@material-tailwind/react";

/* ICONS */
import {
  IoCall,
  IoMail,
  IoLogoFacebook,
  IoLogoInstagram,
  IoLogoLinkedin,
} from "react-icons/io5";

/* CONTAINER ANIMATION */
const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 1 },
  },
};

/* QUICK LINKS */
const quickLinks = [
  {
    id: 1,
    route: "/about-us",
    label: "About Us",
  },
  {
    id: 2,
    route: "/return-policy",
    label: "Return Policy",
  },
  {
    id: 3,
    route: "/privacy-policy",
    label: "Privacy Policy",
  },
  {
    id: 4,
    route: "/terms-conditions",
    label: "Terms & Conditions",
  },
];

/* WE CARE */
const weCareLinks = [
  {
    id: 1,
    route: "/my-account",
    label: "My Account",
  },
  {
    id: 3,
    route: "/faqs",
    label: "FAQs",
  },
  {
    id: 5,
    route: "/contact-us",
    label: "Contact Us",
  },
];

/* SOCIAL MEDIA HANDLES */
const socialMediaHandles = [
  {
    id: 1,
    route: "https://www.facebook.com",
    label: "Facebook",
  },
  {
    id: 2,
    route: "https://www.instagram.com",
    label: "Instagram",
  },
  {
    id: 3,
    route: "https://www.linkedin.com",
    label: "LinkedIn",
  },
];

const Footer = () => {
  return (
    <motion.footer
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-primary"
    >
      <div className="container">
        {/* NEWSLETTER SUBSCRIPTIONS */}
        <div className="p-0">
          <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-0 xl:gap-10 lg:gap-10 items-center">
            <div className="col-span-1">
              <div className="py-5 xl:py-10 lg:py-10 w-full xl:w-4/5 lg:w-4/5">
                <h2 className="text-3xl font-fraunces-bold text-white leading-tight">
                  Subscribe to our newsletter
                </h2>
                <p className="text-lg font-source font-normal text-white text-opacity-70 mt-2">
                  Get the latest news, tips, and exclusive offers delivered
                  straight to your inbox. Sign up now for our newsletter.
                </p>
              </div>
            </div>
            <div className="col-span-1">
              <form className="w-4/5 mx-auto">
                <div className="flex items-center gap-3">
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="w-full px-5 py-2.5 rounded-md bg-white text-base font-source font-normal text-black placeholder:text-sm focus:outline-none"
                    placeholder="Type your email address"
                  />
                  <Button className="shrink-0 text-base font-source font-semibold text-white rounded-md py-2.5 shadow-none hover:shadow-none normal-case text-center bg-primaryRed">
                    Subscribe
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* TOP FOOTER */}
        <div className="py-10 border-t border-white border-opacity-30">
          <div className="grid grid-cols-1 xl:grid-cols-6 lg:grid-cols-6">
            {/* COLUMN 1 */}
            <div className="col-span-3">
              <div className="w-fit bg-white p-2 rounded-lg">
                <img
                  src={require("../assets/images/westcoast-wholesale-logo.png")}
                  className="h-24 w-auto object-contain"
                  alt="West Cost Wholesale"
                />
              </div>
              <div className="mt-5 w-full xl:w-4/5 lg:w-4/5">
                <p className="w-4/5 text-lg font-source font-normal text-white text-opacity-70 leading-relaxed">
                  West Coast Wholesale is your go-to online smoke shop for
                  Puffco glass, Empire Glassworks pieces and more. Trusted
                  headshop for quality glass and fast U.S. shipping.
                </p>
              </div>
            </div>

            {/* COLUMN 2 */}
            <div className="col-span-1 mt-5 xl:mt-0 lg:mt-0">
              <h5 className="text-lg font-source font-bold text-white">
                Quick Links
              </h5>
              <div className="mt-5">
                {quickLinks.map((link) => (
                  <Link
                    to={link.route}
                    key={link.id}
                    className="text-base font-source font-normal text-white text-opacity-70 hover:text-primaryRed block transition-all duration-300 ease-in-out first:mt-0 mt-5"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* COLUMN 3 */}
            <div className="col-span-1 mt-5 xl:mt-0 lg:mt-0">
              <h5 className="text-lg font-source font-bold text-white">
                We Care
              </h5>
              <div className="mt-5">
                {weCareLinks.map((link) => (
                  <Link
                    to={link.route}
                    key={link.id}
                    className="text-base font-source font-normal text-white text-opacity-70 hover:text-primaryRed block transition-all duration-300 ease-in-out first:mt-0 mt-5"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* COLUMN 4 */}
            <div className="col-span-1 mt-10 xl:mt-0 lg:mt-0">
              <h5 className="text-lg font-source font-bold text-white">
                Follow us on
              </h5>
              <div className="mt-5">
                {socialMediaHandles.map((link) => (
                  <Link
                    to={link.route}
                    key={link.id}
                    className="text-base font-source font-normal text-white text-opacity-70 hover:text-primaryRed block transition-all duration-300 ease-in-out first:mt-0 mt-5"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* GET IN TOUCH */}
        <div className="border-t border-white border-opacity-30 py-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 items-center">
            <div className="col-span-1">
              <h5 className="text-2xl font-fraunces-bold tracking-tight text-white">
                Get in touch with us
              </h5>
              <p className="text-sm font-source font-normal text-white text-opacity-80 mt-1">
                Contact us to know more about our products and prices
              </p>
            </div>
            <div className="col-span-2 mt-5 xl:mt-0 lg:mt-0">
              <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-y-5 xl:gap-0 lg:gap-0 items-center">
                <div className="col-span-1">
                  <div className="flex items-center gap-5 group cursor-pointer">
                    <div className="w-12 h-12 bg-white bg-opacity-5 rounded-full shrink-0 flex items-center justify-center">
                      <IoCall className="w-5 h-5 text-white group-hover:text-logoOrange transition-all duration-300 ease-in-out" />
                    </div>
                    <div>
                      <h5 className="text-sm font-source font-semibold text-white text-opacity-80">
                        Give us a call
                      </h5>
                      <a
                        href="tel:+18779279526"
                        className="block text-lg font-source font-semibold text-white group-hover:text-logoOrange transition-all duration-300 ease-in-out"
                      >
                        +1 (877) 927-9526
                      </a>
                    </div>
                  </div>
                </div>
                <div className="col-span-1">
                  <div className="flex items-center gap-5 group cursor-pointer">
                    <div className="w-12 h-12 bg-white bg-opacity-5 rounded-full shrink-0 flex items-center justify-center">
                      <IoMail className="w-5 h-5 text-white group-hover:text-logoOrange transition-all duration-300 ease-in-out" />
                    </div>
                    <div>
                      <h5 className="text-sm font-source font-semibold text-white text-opacity-80">
                        Send us an email
                      </h5>
                      <a
                        href="mailto:sales@westcoastwholesale.com"
                        className="block text-lg font-source font-semibold text-white group-hover:text-logoOrange transition-all duration-300 ease-in-out"
                      >
                        sales@westcoastwholesale.com
                      </a>
                    </div>
                  </div>
                </div>
                <div className="col-span-1">
                  <div className="flex items-center justify-start xl:justify-end lg:justify-end gap-5">
                    <h5 className="text-sm font-source font-semibold text-white text-opacity-80">
                      Follow us on
                    </h5>
                    <div className="flex items-center gap-2">
                      <a
                        href="https://www.facebook.com/FoodFarmacy.co"
                        target="_blank"
                        rel="noreferrer"
                        className="w-10 h-10 bg-white bg-opacity-5 rounded-full shrink-0 flex items-center justify-center text-white hover:text-logoOrange transition-all duration-300 ease-in-out"
                      >
                        <IoLogoFacebook className="w-5 h-5" />
                      </a>
                      <a
                        href="https://www.instagram.com/foodfarmacy.co"
                        target="_blank"
                        rel="noreferrer"
                        className="w-10 h-10 bg-white bg-opacity-5 rounded-full shrink-0 flex items-center justify-center text-white hover:text-logoOrange transition-all duration-300 ease-in-out"
                      >
                        <IoLogoInstagram className="w-5 h-5" />
                      </a>
                      <a
                        href="https://www.linkedin.com/company/foodfarmacy/"
                        target="_blank"
                        rel="noreferrer"
                        className="w-10 h-10 bg-white bg-opacity-5 rounded-full shrink-0 flex items-center justify-center text-white hover:text-logoOrange transition-all duration-300 ease-in-out"
                      >
                        <IoLogoLinkedin className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* COPYRIGHT FOOTER */}
        <div className="py-3 border-t border-white border-opacity-30">
          <div className="block xl:flex lg:flex items-center justify-center xl:justify-between lg:justify-between">
            <p className="text-sm font-source font-normal text-white">
              Copyright {moment(new Date()).format("YYYY")} © West Coast
              Wholesale. All right reserved. Powered by{" "}
              <a
                href="https://www.roundtechsquare.com"
                target="_blank"
                rel="noreferrer"
                className="text-rtsColor"
              >
                RoundTechSquare
              </a>
            </p>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;
